import { Select } from "@/components/ui/Select";
import type { SelectOption } from "@/components/ui/Select";
import { Input } from "@/components/ui/input";
import { Button, cn } from "@paribus/shared-ui";
import { ArrowDown, ArrowUp, Plus, Trash2 } from "lucide-react";

import type { CitationDraftRow, CitationRowErrors } from "../types";
import { createCitationDraftRow } from "../utils/visitCitations";

interface VisitCitationRowsEditorProps {
  rows: CitationDraftRow[];
  /** Index-aligned with rows; empty while errors are hidden. */
  rowErrors: CitationRowErrors[];
  fileOptions: SelectOption[];
  onChange: (rows: CitationDraftRow[]) => void;
}

export const VisitCitationRowsEditor = ({
  rows,
  rowErrors,
  fileOptions,
  onChange,
}: VisitCitationRowsEditorProps) => {
  const updateRow = (key: string, patch: Partial<CitationDraftRow>) =>
    onChange(rows.map((row) => (row.key === key ? { ...row, ...patch } : row)));
  const moveRow = (index: number, offset: -1 | 1) => {
    const reordered = [...rows];
    [reordered[index], reordered[index + offset]] = [reordered[index + offset], reordered[index]];
    onChange(reordered);
  };

  return (
    <div className="space-y-2">
      {rows.map((row, index) => {
        const errors = rowErrors[index] ?? {};
        const messages = [...new Set([errors.file_id, errors.start_page, errors.end_page])].filter(
          Boolean
        );
        const errorId = `citation-${row.key}-error`;
        const label = `Citation ${index + 1}`;
        return (
          <div key={row.key} className="space-y-1">
            <div className="flex items-center gap-2">
              <Select
                value={row.file_id}
                onChange={(fileId) => updateRow(row.key, { file_id: fileId })}
                options={fileOptions}
                placeholder="Select file"
                ariaLabel={`${label} file`}
                ariaDescribedBy={messages.length ? errorId : undefined}
                ariaInvalid={Boolean(errors.file_id) || undefined}
                className={cn(
                  "h-9 min-w-0 flex-1 rounded-md border bg-background px-3 text-sm",
                  errors.file_id
                    ? "border-destructive focus-visible:shadow-focus-danger"
                    : "border-input focus-visible:shadow-focus"
                )}
              />
              <Input
                size="sm"
                inputMode="numeric"
                placeholder="Start"
                aria-label={`${label} start page`}
                aria-describedby={messages.length ? errorId : undefined}
                invalid={Boolean(errors.start_page)}
                value={row.start_page}
                onChange={(event) => updateRow(row.key, { start_page: event.target.value })}
                className="w-20"
              />
              <span aria-hidden="true" className="text-sm text-muted-foreground">
                –
              </span>
              <Input
                size="sm"
                inputMode="numeric"
                placeholder="End"
                aria-label={`${label} end page`}
                aria-describedby={messages.length ? errorId : undefined}
                invalid={Boolean(errors.end_page)}
                value={row.end_page}
                onChange={(event) => updateRow(row.key, { end_page: event.target.value })}
                className="w-20"
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                aria-label={`Move ${label.toLowerCase()} up`}
                disabled={index === 0}
                onClick={() => moveRow(index, -1)}
                className="px-2"
              >
                <ArrowUp />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                aria-label={`Move ${label.toLowerCase()} down`}
                disabled={index === rows.length - 1}
                onClick={() => moveRow(index, 1)}
                className="px-2"
              >
                <ArrowDown />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                aria-label={`Remove ${label.toLowerCase()}`}
                onClick={() => onChange(rows.filter((candidate) => candidate.key !== row.key))}
                className="px-2"
              >
                <Trash2 />
              </Button>
            </div>
            {messages.length > 0 && (
              <p id={errorId} className="text-xs text-destructive-text">
                {messages.join(". ")}
              </p>
            )}
          </div>
        );
      })}
      <Button
        type="button"
        variant="link"
        size="sm"
        leadingIcon={<Plus />}
        onClick={() => onChange([...rows, createCitationDraftRow()])}
      >
        Add citation
      </Button>
    </div>
  );
};
